/**
 * Last-known device permission hints (camera/mic).
 *
 * This is NOT the source of truth — the browser APIs are:
 * - getUserMedia / Permissions API ("camera", "microphone")
 *
 * We mirror a resolved "granted" | "denied" into three first-party stores so UX
 * heuristics survive Safari ITP, "Block all cookies", and storage tier differences:
 *
 *   1. localStorage  — primary (Chrome, Firefox, most Safari sessions)
 *   2. Cookie        — fallback when localStorage is blocked or cleared (WebKit)
 *   3. sessionStorage — same-tab fallback when persistent tiers fail
 *
 * Read order prefers the longest-lived tier; write updates every tier that is available.
 * Only "granted" and "denied" are stored — never "default" / "prompt".
 */

import { getCookie, hasCookie, removeCookie, setCookie } from "./cookies";

/** @readonly */
export const DEVICE_PERMISSION_STORAGE_KEYS = {
  MEDIA_CAPTURE: "jh_deviceMediaCapturePermission",
};

const MEDIA_KEY = DEVICE_PERMISSION_STORAGE_KEYS.MEDIA_CAPTURE;

/** Pre-jh_ cookie name — migrated once, then removed. */
const LEGACY_MEDIA_COOKIE = "deviceMediaCapturePermission";

const VALID_PERMISSIONS = new Set(["granted", "denied"]);

const isBrowser = () => typeof window !== "undefined";

const migrationFlagKey = (storageKey) => `${storageKey}__legacyMigrated`;

const logDevWarn = (message, error) => {
  if (process.env.NODE_ENV === "production") return;
  if (error !== undefined) {
    console.warn(message, error);
    return;
  }
  console.warn(message);
};

/* -------------------------------------------------------
   TIER IMPLEMENTATIONS
-------------------------------------------------------- */

const tiers = {
  local: {
    get(key) {
      if (!isBrowser()) return undefined;
      try {
        return window.localStorage.getItem(key) || undefined;
      } catch (err) {
        logDevWarn("localStorage get failed:", err);
        return undefined;
      }
    },
    set(key, value) {
      if (!isBrowser()) return false;
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch (err) {
        logDevWarn("localStorage set failed:", err);
        return false;
      }
    },
    remove(key) {
      if (!isBrowser()) return;
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
        logDevWarn("localStorage remove failed:", err);
      }
    },
  },
  session: {
    get(key) {
      if (!isBrowser()) return undefined;
      try {
        return window.sessionStorage.getItem(key) || undefined;
      } catch (err) {
        logDevWarn("sessionStorage get failed:", err);
        return undefined;
      }
    },
    set(key, value) {
      if (!isBrowser()) return false;
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (err) {
        logDevWarn("sessionStorage set failed:", err);
        return false;
      }
    },
    remove(key) {
      if (!isBrowser()) return;
      try {
        window.sessionStorage.removeItem(key);
      } catch (err) {
        logDevWarn("sessionStorage remove failed:", err);
      }
    },
  },
  cookie: {
    get(key) {
      if (!isBrowser()) return undefined;
      try {
        return getCookie(key) || undefined;
      } catch (err) {
        logDevWarn("cookie get failed:", err);
        return undefined;
      }
    },
    set(key, value) {
      if (!isBrowser()) return false;
      try {
        return setCookie(key, value);
      } catch (err) {
        logDevWarn("cookie set failed:", err);
        return false;
      }
    },
    remove(key) {
      if (!isBrowser() || !hasCookie(key)) return;
      try {
        removeCookie(key);
      } catch (err) {
        logDevWarn("cookie remove failed:", err);
      }
    },
  },
};

/** Read order: longest-lived first. */
const READ_TIER_ORDER = ["local", "cookie", "session"];

/** Write all tiers so any surviving tier can re-hydrate the others on next read. */
const WRITE_TIER_ORDER = ["local", "cookie", "session"];

/* -------------------------------------------------------
   NORMALIZATION
-------------------------------------------------------- */

const normalizePermission = (value) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().toLowerCase();
  return trimmed || undefined;
};

/** @returns {"granted"|"denied"|undefined} */
const toValidPermission = (value) => {
  const normalized = normalizePermission(value);
  return VALID_PERMISSIONS.has(normalized) ? normalized : undefined;
};

/* -------------------------------------------------------
   TIERED READ / WRITE / REMOVE
-------------------------------------------------------- */

/**
 * @param {string} storageKey
 * @returns {"granted"|"denied"|undefined}
 */
const readFromTiers = (storageKey) => {
  for (const tierName of READ_TIER_ORDER) {
    const value = toValidPermission(tiers[tierName].get(storageKey));
    if (value) return value;
  }
  return undefined;
};

/**
 * @param {string} storageKey
 * @param {"granted"|"denied"} value
 * @returns {boolean} true if at least one tier accepted the write
 */
const writeToAllTiers = (storageKey, value) => {
  let anyOk = false;
  for (const tierName of WRITE_TIER_ORDER) {
    if (tiers[tierName].set(storageKey, value)) {
      anyOk = true;
    }
  }
  return anyOk;
};

const removeFromAllTiers = (storageKey) => {
  for (const tierName of WRITE_TIER_ORDER) {
    tiers[tierName].remove(storageKey);
  }
};

/**
 * After reading from any tier, copy into tiers that are missing the value.
 * @param {string} storageKey
 * @param {"granted"|"denied"} value
 */
const reconcileTiers = (storageKey, value) => {
  for (const tierName of WRITE_TIER_ORDER) {
    const current = toValidPermission(tiers[tierName].get(storageKey));
    if (current !== value) {
      tiers[tierName].set(storageKey, value);
    }
  }
};

/** @returns {boolean} */
const canUseLocalStorage = () => {
  if (!isBrowser()) return false;
  const probe = "__jh_perm_storage_probe__";
  if (!tiers.local.set(probe, "1")) return false;
  tiers.local.remove(probe);
  return true;
};

/* -------------------------------------------------------
   LEGACY COOKIE MIGRATION
-------------------------------------------------------- */

const removeLegacyCookieIfPresent = (legacyName) => {
  if (!isBrowser() || !hasCookie(legacyName)) return;
  try {
    removeCookie(legacyName);
  } catch (err) {
    logDevWarn("Legacy cookie removal failed:", err);
  }
};

const migrateLegacyCookie = (legacyCookieKey, storageKey) => {
  const fromLegacy = toValidPermission(getCookie(legacyCookieKey));
  if (!fromLegacy) return undefined;

  writeToAllTiers(storageKey, fromLegacy);
  removeLegacyCookieIfPresent(legacyCookieKey);
  return fromLegacy;
};

const hasMigrationFlag = (storageKey) => {
  const flagKey = migrationFlagKey(storageKey);
  return Boolean(readFromTiers(flagKey));
};

const setMigrationFlag = (storageKey) => {
  writeToAllTiers(migrationFlagKey(storageKey), "1");
};

/* -------------------------------------------------------
   PERMISSION STORE
-------------------------------------------------------- */

/**
 * @param {string} storageKey
 * @param {string} legacyCookieKey
 */
const createPermissionStore = (storageKey, legacyCookieKey) => {
  const read = () => {
    const stored = readFromTiers(storageKey);
    if (stored) {
      reconcileTiers(storageKey, stored);
      return stored;
    }

    if (hasMigrationFlag(storageKey)) {
      return undefined;
    }

    if (canUseLocalStorage()) {
      setMigrationFlag(storageKey);
    }

    return migrateLegacyCookie(legacyCookieKey, storageKey);
  };

  const persist = (permission) => {
    const normalized = normalizePermission(permission);
    if (!VALID_PERMISSIONS.has(normalized)) return false;

    if (readFromTiers(storageKey) === normalized) {
      return true;
    }

    removeLegacyCookieIfPresent(legacyCookieKey);
    return writeToAllTiers(storageKey, normalized);
  };

  const clear = () => {
    removeFromAllTiers(storageKey);
    removeFromAllTiers(migrationFlagKey(storageKey));
    removeLegacyCookieIfPresent(legacyCookieKey);
  };

  return { read, persist, clear };
};

const mediaStore = createPermissionStore(MEDIA_KEY, LEGACY_MEDIA_COOKIE);

/* -------------------------------------------------------
   PUBLIC API
-------------------------------------------------------- */

/** @param {"granted"|"denied"} permission */
export const persistDeviceMediaCapturePermission = (permission) =>
  mediaStore.persist(permission);

/** @returns {"granted"|"denied"|undefined} */
export const getPersistedDeviceMediaCapturePermission = () => mediaStore.read();

export const clearPersistedDeviceMediaCapturePermission = () => mediaStore.clear();
